import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  TextField,
  styled,
} from "@mui/material";
import { useState } from "react";
import PropTypes from "prop-types";
import YouTubeIcon from "@mui/icons-material/YouTube";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import FitifyLogo from "assets/images/nFitifyLogo.svg";

// Styled Components for Email Input
const EmailInputBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "16px",
});

const EmailInput = styled("input")({
  flex: 1,
  padding: "14px 20px",
  backgroundColor: "rgba(54, 54, 54, 0.3)",
  border: "none",
  borderRadius: "20px",
  color: "#FFFFFF",
  fontSize: "16px",
  fontFamily: "Outfit, sans-serif",
  outline: "none",
  backdropFilter: "blur(10px)",
  "&::placeholder": {
    color: "rgba(255, 255, 255, 0.8)",
  },
  "&:focus": {
    outline: "none",
    backgroundColor: "rgba(54, 54, 54, 0.5)",
  },
});

const GetInTouchButton = styled(Button)({
  backgroundColor: "#FFFFFF",
  color: "#242424",
  padding: "14px 24px",
  borderRadius: "16px",
  fontSize: "16px",
  fontWeight: 600,
  textTransform: "none",
  fontFamily: "Outfit, sans-serif",
  minWidth: "140px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
  },
  transition: "all 0.3s ease",
});

const FooterSection = () => {
  const [email, setEmail] = useState("");

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleGetInTouch = () => {
    console.log("Email submitted:", email);
    // Handle email submission logic here
  };

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#1A1A1A",
        zIndex: 11,
      }}
    >
      {/* Background Image with Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
        }}
      >
        <Box
          component="img"
          src={require("assets/images/footer.png")}
          alt="Footer Background"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "top",
          }}
        />
        {/* Dark Teal Overlay */}
      </Box>

      {/* Content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 3,
          pt: { xs: 6, md: 8 },
          pb: 2,
        }}
      >
        <Container maxWidth="xl">
          {/* Main Footer Content - Two Main Sections */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              gap: { xs: 4, md: 8 },
              mb: 6,
            }}
          >
            {/* Left Section - Fitify Branding and Newsletter */}
            <Box sx={{ flex: 1, minWidth: 0, maxWidth: "500px" }}>
              {/* Logo */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box
                  component="img"
                  src={FitifyLogo}
                  alt="Fitify Logo"
                  sx={{
                    height: { xs: "35px", md: "45px" },
                    width: "auto",
                  }}
                />
              </Box>

              {/* Tagline */}
              <Typography
                sx={{
                  color: "#FFFFFF",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  mb: 5,
                  fontFamily: "Outfit",
                  opacity: 0.9,
                }}
              >
                Empowering dietitians to grow smarter.
              </Typography>

              {/* Newsletter Signup */}
              <Box>
                <Typography
                  sx={{
                    color: "#FFFFFF",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    mb: 2,
                    fontFamily: "Outfit",
                  }}
                >
                  Stay up to date
                </Typography>

                {/* <EmailInputBox>
                  <EmailInput
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="Enter your email ID"
                  />
                  <GetInTouchButton onClick={handleGetInTouch}>
                    Get in touch
                  </GetInTouchButton>
                </EmailInputBox> */}

                <Typography
                  sx={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "0.8rem",
                    fontFamily: "Outfit",
                  }}
                >
                  By getting in touch you agree to our{" "}
                  <Typography
                    component="span"
                    sx={{
                      color: "#FFFFFF",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontFamily: "Outfit",
                    }}
                  >
                    privacy policy
                  </Typography>
                </Typography>
              </Box>
            </Box>

            {/* Right Section - Services and Social Media */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: { xs: 4, md: 8 },
                flex: 1,
              }}
            >
              {/* Our Services */}
              <Box sx={{ minWidth: 0, maxWidth: "300px" }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#FFFFFF",
                    fontSize: { xs: "1.2rem", md: "1.3rem" },
                    fontWeight: "bold",
                    mb: 4,
                    fontFamily: "Outfit",
                  }}
                >
                  Our Services
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {[
                    "Client/ Business Management",
                    "Diet Planning",
                    "Billing & Invoice",
                    "Sales & Marketing",
                  ].map((service, index) => (
                    <Typography
                      key={index}
                      sx={{
                        color: "#FFFFFF",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        fontFamily: "Outfit",
                        cursor: "pointer",
                        opacity: 0.9,
                        "&:hover": {
                          opacity: 1,
                          transform: "translateX(5px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {service}
                    </Typography>
                  ))}
                </Box>
              </Box>

              {/* Follow us on */}
              <Box sx={{ minWidth: 0, maxWidth: "250px" }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#FFFFFF",
                    fontSize: { xs: "1.2rem", md: "1.3rem" },
                    fontWeight: "bold",
                    mb: 4,
                    fontFamily: "Outfit",
                  }}
                >
                  Follow us on:
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {[
                    {
                      name: "Youtube",
                      icon: (
                        <YouTubeIcon
                          sx={{ fontSize: "1.5rem", color: "#FFFFFF" }}
                        />
                      ),
                    },
                    {
                      name: "Twitter",
                      icon: (
                        <TwitterIcon
                          sx={{ fontSize: "1.5rem", color: "#FFFFFF" }}
                        />
                      ),
                    },
                    {
                      name: "Instagram",
                      icon: (
                        <InstagramIcon
                          sx={{ fontSize: "1.5rem", color: "#FFFFFF" }}
                        />
                      ),
                    },
                    {
                      name: "Facebook",
                      icon: (
                        <FacebookIcon
                          sx={{ fontSize: "1.5rem", color: "#FFFFFF" }}
                        />
                      ),
                    },
                  ].map((social, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        cursor: "pointer",
                        opacity: 0.9,
                        "&:hover": {
                          opacity: 1,
                          transform: "translateX(5px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {social.icon}
                      <Typography
                        sx={{
                          color: "#FFFFFF",
                          fontSize: { xs: "1rem", md: "1.1rem" },
                          fontFamily: "Outfit",
                        }}
                      >
                        {social.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Bottom Section - Copyright and Legal Links */}
          <Box
            sx={{
              borderTop: "1px solid rgba(255, 255, 255, 0.2)",
              mt: 6,
              pt: 3,
              pb: 0,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Typography
              sx={{
                color: "#FFFFFF",
                fontSize: "1rem",
                fontFamily: "Outfit",
                opacity: 0.8,
              }}
            >
              Copyright © 2025. All rights reserved.
            </Typography>

            <Box sx={{ display: "flex", gap: 4 }}>
              {["Privacy policy", "Terms of Service", "Cookie Settings"].map(
                (link, index) => (
                  <Typography
                    key={index}
                    sx={{
                      color: "#FFFFFF",
                      fontSize: "1rem",
                      fontFamily: "Outfit",
                      cursor: "pointer",
                      opacity: 0.8,
                      "&:hover": {
                        opacity: 1,
                        textDecoration: "underline",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {link}
                  </Typography>
                )
              )}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default FooterSection;
